import { Loader } from '../../Widgets';
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Party, User } from 'service';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faFileInvoice, faPlus, faReceipt, faUsers } from '@fortawesome/free-solid-svg-icons';
import ArthaGateway from 'service/ArthaGateway';
import { app_version, clearCache } from 'service/Utils';
import toast from 'react-hot-toast';
import { NewCustomer } from '../../Widgets/NewCustomer';
import { ActionCreators } from 'store/actionCreators';

type ErrorBoundaryState = {
    hasError: boolean;
}

class DashboardErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error) {
        console.error('Dashboard render error:', error);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="page-dashboard">
                    <div className="container py-4">
                        <div className="alert alert-danger" role="alert">
                            <h5 className="mb-2">Something went wrong</h5>
                            <p className="mb-3">We could not render this page. Please reload and try again.</p>
                            <button className="btn btn-dark btn-sm" onClick={() => window.location.reload()}>
                                Reload Page
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const DashboardContent = () => {

    const userData = useSelector((state: RootState) => state.appdata);
    const [user, setUser] = useState<User>();
    const [customers, setCustomers] = useState<Party[]>([]);
    const [showNewCustomer, setShowNewCustomer] = useState(false);
    const [customerName, setCustomerName] = useState<string>('');
    const [hasSearchedCustomers, setHasSearchedCustomers] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [pageError, setPageError] = useState<string | null>(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const openInvoiceCreation = () => {
        toast('Select a customer to create a new invoice', { position: 'bottom-center' });
        navigate('/customers');
    };

    useEffect(() => {
        if (userData.profile) {
            setUser(userData.profile.profile);
            setIsLoading(false);
            setPageError(null);
        }
    }, [userData]);

    const getErrorMessage = (error: any, fallback: string) => {
        if (error?.response?.data?.message) {
            return error.response.data.message;
        }
        if (error?.message) {
            return error.message;
        }
        return fallback;
    }

    const handleSearchCustomer = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length < 3) {
            setCustomers([]);
            setHasSearchedCustomers(false);
            setPageError(null);
            return;
        }
        searchCustomer(e.target.value);
    }

    const searchCustomer = (name: string) => {
        setCustomers([]);
        setCustomerName(name);
        setHasSearchedCustomers(true);
        ArthaGateway.getCustomers(name).then((response) => {
            console.log(response);
            setCustomers(response.entries);
            setPageError(null);
        }).catch((error) => {
            const message = getErrorMessage(error, 'Error fetching customer details.');
            setPageError(message);
            toast.error(message);
            console.log(error);
        });
    }

    const handleCreateNewCustomer = () => {
        setShowNewCustomer(true);
    };

    const handleCloseNewCustomer = () => {
        setShowNewCustomer(false);
    };

    const handleNewCustomerAdded: (newCustomerName?: string) => void = (newCustomerName) => {
        setShowNewCustomer(false);
        searchCustomer(newCustomerName || '');
        setPageError(null);
        toast.success("Customer added successfully");
    };

    const createNewInvoice = (customer: Party) => {
        dispatch(ActionCreators.selectCustomer(customer));
        navigate("/invoice/new");
    }

    const openCustomerProfile = (customer: Party) => {
        dispatch(ActionCreators.selectCustomer(customer));
        navigate('/customer/' + customer.id, { state: { customer } });
    }

    const actions = [
        {
            title: 'View Invoices',
            subtitle: 'Open invoice list',
            icon: faFileInvoice,
            link: '/invoices',
            tone: 'dark'
        },
        {
            title: 'Create Invoice',
            subtitle: 'Start billing now',
            icon: faPlus,
            onClick: openInvoiceCreation,
            tone: 'primary'
        },
        {
            title: 'View Receipts',
            subtitle: 'Track collections',
            icon: faReceipt,
            link: '/receipts',
            tone: 'dark'
        },
        {
            title: 'Create Receipt',
            subtitle: 'Add payment entry',
            icon: faPlus,
            link: '/receipt/new',
            tone: 'primary'
        },
        {
            title: 'View Customers',
            subtitle: 'Search and manage',
            icon: faUsers,
            link: '/customers',
            tone: 'dark'
        },
        {
            title: 'Create Customer',
            subtitle: 'Add a new party',
            icon: faPlus,
            onClick: handleCreateNewCustomer,
            tone: 'primary'
        }
    ];

    return (
        <div className="page-dashboard app-page-shell">
            {isLoading &&
                <Loader isFullHeight={true} />
            }
            {!isLoading &&
                <div className="container">
                    {!showNewCustomer &&
                        <>
                            <div className='dashboard-hero mb-3'>
                                <div className='dashboard-hero-content'>
                                    <div className='dashboard-greeting'>Welcome back</div>
                                    <h2>{user?.name}</h2>
                                    <p></p>
                                </div>
                                <div className='dashboard-meta'>
                                    <div className="btn btn-sm btn-outline-light" onClick={clearCache}>Update to latest version</div>
                                    <div className='small mt-2'>{app_version}</div>
                                </div>
                            </div>

                            <div className='dashboard-search-block mb-3'>
                                <input
                                    type="text"
                                    className="form-control dashboard-search-input"
                                    placeholder='Search customer by name or mobile'
                                    onChange={(e) => handleSearchCustomer(e)}
                                    autoFocus
                                />

                                {pageError &&
                                    <div className="alert alert-danger mb-2" role="alert">
                                        <strong>Error:</strong> {pageError}
                                    </div>
                                }

                                {customers.length > 0 &&
                                    <div className='customers-list app-panel mt-2'>
                                        {customers.map((customer, index) => (
                                            <div className='customer-row' key={index}>
                                                <div className='d-flex justify-content-between align-items-start gap-2'>
                                                    <div>
                                                        <div className='fw-bold'>{customer.name}</div>
                                                        <div className='text-muted small'>{customer.mobileNumber}</div>
                                                    </div>
                                                    <div className='d-flex gap-2 customer-actions'>
                                                        <button type='button' className='btn btn-outline-dark btn-sm customer-action-btn' title='View Customer' aria-label='View Customer' onClick={() => openCustomerProfile(customer)}>
                                                            <FontAwesomeIcon icon={faEye} />
                                                            <span className='d-none d-md-inline ms-1'>View</span>
                                                        </button>
                                                        <button type='button' className='btn btn-dark btn-sm customer-action-btn' title='Create Invoice' aria-label='Create Invoice' onClick={() => createNewInvoice(customer)}>
                                                            <FontAwesomeIcon icon={faFileInvoice} />
                                                            <span className='d-none d-md-inline ms-1'>New Invoice</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                }

                                {hasSearchedCustomers && customers.length === 0 && !pageError &&
                                    <div className='app-empty-state mt-2'>
                                        No customers found.
                                    </div>
                                }
                            </div>

                            <div className='app-panel'>
                                <div className='app-page-header mb-2'>
                                    <h2>Quick Actions</h2>
                                </div>
                                <div className='row g-2'>
                                    {actions.map((action, index) => (
                                        <div className="col-6 col-md-4" key={index}>
                                            {action.link &&
                                                <NavLink to={action.link} className={"dashboard-action-tile " + action.tone}>
                                                    <div className='action-icon'><FontAwesomeIcon icon={action.icon} /></div>
                                                    <div className='action-title'>{action.title}</div>
                                                    <div className='action-subtitle'>{action.subtitle}</div>
                                                </NavLink>
                                            }
                                            {action.onClick &&
                                                <button type='button' className={"dashboard-action-tile " + action.tone} onClick={action.onClick}>
                                                    <div className='action-icon'><FontAwesomeIcon icon={action.icon} /></div>
                                                    <div className='action-title'>{action.title}</div>
                                                    <div className='action-subtitle'>{action.subtitle}</div>
                                                </button>
                                            }
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    }
                    {showNewCustomer && (
                        <NewCustomer
                            name={customerName}
                            onCustomerAdded={handleNewCustomerAdded}
                            onClose={handleCloseNewCustomer}
                        />
                    )}
                </div>
            }
        </div>
    );
};

export const Dashboard = () => {
    return (
        <DashboardErrorBoundary>
            <DashboardContent />
        </DashboardErrorBoundary>
    );
};